/**
 * Where a portal-navbar dropdown (More, the Merlin switcher) pins itself.
 *
 * The dense tab row scrolls sideways (overflow-x: auto), and an overflow
 * container clips anything hanging below it. So the dropdowns are
 * position: fixed, pinned under the 56px bar, and anchored to where their
 * trigger sat when they opened: `right` for a right-aligned menu, `centerX`
 * for a centred one. Given the menu's width, both keep the whole menu at
 * least EDGE_GAP px inside the viewport on both sides.
 */

export const NAV_DROPDOWN_TOP = 62;
const EDGE_GAP = 12;

export interface NavDropdownAnchor {
  /** Distance from the viewport's right edge to the trigger's right edge. */
  right: number;
  /** Viewport x of the trigger's centre, clamped so a menu of `menuWidth` fits. */
  centerX: number;
}

/** Anchor for `trigger`; 12px in from the right, centred, when there is nothing to measure. */
export function navDropdownAnchor(trigger: HTMLElement | null, menuWidth = 0): NavDropdownAnchor {
  if (!trigger || typeof window === 'undefined') {
    return { right: EDGE_GAP, centerX: EDGE_GAP + menuWidth / 2 };
  }
  const rect = trigger.getBoundingClientRect();
  const width = window.innerWidth;
  const half = menuWidth / 2;
  const centerX = Math.min(Math.max(rect.left + rect.width / 2, EDGE_GAP + half), width - EDGE_GAP - half);
  // A right-aligned menu of `menuWidth` must not run past the LEFT edge
  // either (a phone-width bar puts the trigger near the left).
  const rightMax = Math.max(EDGE_GAP, width - EDGE_GAP - menuWidth);
  return {
    right: Math.round(Math.min(Math.max(width - rect.right, EDGE_GAP), rightMax)),
    centerX: Math.round(centerX),
  };
}
